import type { AppContext } from '../app';
import {
  DEFAULT_HELPER_CONFIG,
  type ChatMessage,
  type HelperConfig,
  type HelperState,
} from './types';

/**
 * PersonalHelper — Phase 70 scaffold.
 *
 * Owns one @mlc-ai/web-llm engine per AppContext. Lazily loads the model
 * on the first ensureReady() call (model weights are multi-GB; we do not
 * pay that cost on every unlock). All WebLLM imports are dynamic so the
 * vitest + jsdom environment never sees the WebGPU-only module.
 *
 * Tool definitions, BYO-key tier, and full feed-summarizer / walkthrough-
 * runner / safety-checker tools are deferred follow-ups. This scaffold
 * lands the lifecycle (idle → loading → ready → generating) + the chat
 * primitive so the FAB + surface can wire to a real backend.
 */
export class PersonalHelper {
  readonly #ctx: AppContext;
  #config: HelperConfig;
  #state: HelperState = { kind: 'idle' };
  #messages: ChatMessage[] = [];
  #engine: unknown = null;
  readonly #stateSubs = new Set<(s: HelperState) => void>();
  readonly #messageSubs = new Set<(m: readonly ChatMessage[]) => void>();

  constructor(ctx: AppContext, config: HelperConfig = DEFAULT_HELPER_CONFIG) {
    this.#ctx = ctx;
    this.#config = config;
    if (!supportsWebGpu()) {
      this.#state = {
        kind: 'unsupported',
        reason: 'WebGPU is required for on-device inference. Try a recent Chrome, Edge, or Safari Tech Preview.',
      };
    }
  }

  /** Read-only snapshot of the current lifecycle state. */
  state(): HelperState {
    return this.#state;
  }

  /** Read-only snapshot of the chat transcript (newest last). */
  messages(): readonly ChatMessage[] {
    return this.#messages;
  }

  /** Read-only snapshot of the active config. */
  config(): HelperConfig {
    return this.#config;
  }

  /** Replace the active config (model id, system prompt). Resets the engine if model id changed. */
  setConfig(next: HelperConfig): void {
    const modelChanged = next.modelId !== this.#config.modelId;
    this.#config = next;
    if (modelChanged) {
      this.#engine = null;
      if (this.#state.kind === 'ready') {
        this.#updateState({ kind: 'idle' });
      }
    }
  }

  /** Subscribe to state transitions. Fires immediately with the current state. */
  onState(fn: (s: HelperState) => void): () => void {
    this.#stateSubs.add(fn);
    fn(this.#state);
    return () => this.#stateSubs.delete(fn);
  }

  /** Subscribe to message-list changes. Fires immediately with the current list. */
  onMessages(fn: (m: readonly ChatMessage[]) => void): () => void {
    this.#messageSubs.add(fn);
    fn(this.#messages);
    return () => this.#messageSubs.delete(fn);
  }

  /**
   * Lazily load the model. No-op if already 'ready' or 'unsupported'.
   * Resolves once the engine is ready to take prompts.
   */
  async ensureReady(): Promise<void> {
    if (this.#state.kind === 'ready' || this.#state.kind === 'generating') return;
    if (this.#state.kind === 'unsupported') {
      throw new Error(this.#state.reason);
    }
    if (this.#state.kind === 'loading') {
      // Already loading — wait for it to land or fail.
      return new Promise<void>((resolve, reject) => {
        const off = this.onState((s) => {
          if (s.kind === 'ready') { off(); resolve(); }
          else if (s.kind === 'error') { off(); reject(new Error(s.message)); }
          else if (s.kind === 'unsupported') { off(); reject(new Error(s.reason)); }
        });
      });
    }
    this.#updateState({ kind: 'loading', progress: 0, status: 'initializing' });
    try {
      const webllm = await import('@mlc-ai/web-llm') as {
        CreateMLCEngine: (
          modelId: string,
          opts: { initProgressCallback: (p: { progress: number; text: string }) => void },
        ) => Promise<unknown>;
      };
      this.#engine = await webllm.CreateMLCEngine(this.#config.modelId, {
        initProgressCallback: (p) => {
          // Coerce out-of-range progress values to [0,1] for the UI.
          const clamped = Math.max(0, Math.min(1, p.progress));
          this.#updateState({ kind: 'loading', progress: clamped, status: p.text });
        },
      });
      this.#updateState({ kind: 'ready' });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.#updateState({ kind: 'error', message });
      throw err;
    }
  }

  /**
   * Send a user message and stream the assistant reply.
   * Appends both messages to the transcript. onToken fires per generated chunk.
   * Resolves with the full assistant reply.
   */
  async chat(userText: string, onToken?: (chunk: string) => void): Promise<string> {
    await this.ensureReady();
    const userMsg: ChatMessage = { role: 'user', content: userText, ts: nowIso() };
    this.#appendMessage(userMsg);
    this.#updateState({ kind: 'generating' });
    try {
      const engine = this.#engine as null | {
        chat: {
          completions: {
            create: (req: {
              messages: Array<{ role: string; content: string }>;
              stream: true;
            }) => AsyncIterable<{ choices: Array<{ delta: { content?: string } }> }>;
          };
        };
      };
      if (engine === null) throw new Error('engine not initialized');
      const systemContent = this.#config.systemPrompt ?? DEFAULT_HELPER_CONFIG.systemPrompt ?? '';
      const reqMessages: Array<{ role: string; content: string }> = [];
      if (systemContent !== '') reqMessages.push({ role: 'system', content: systemContent });
      for (const m of this.#messages) {
        reqMessages.push({ role: m.role, content: m.content });
      }
      const stream = await engine.chat.completions.create({ messages: reqMessages, stream: true });
      let full = '';
      for await (const chunk of stream) {
        const delta = chunk.choices[0]?.delta?.content ?? '';
        if (delta !== '') {
          full += delta;
          onToken?.(delta);
        }
      }
      const assistantMsg: ChatMessage = { role: 'assistant', content: full, ts: nowIso() };
      this.#appendMessage(assistantMsg);
      this.#updateState({ kind: 'ready' });
      return full;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.#updateState({ kind: 'error', message });
      throw err;
    }
  }

  /** Clear the chat transcript. Does not reset the engine. */
  clearMessages(): void {
    this.#messages = [];
    this.#emitMessages();
  }

  #updateState(next: HelperState): void {
    this.#state = next;
    for (const fn of this.#stateSubs) fn(next);
  }

  #appendMessage(m: ChatMessage): void {
    this.#messages = [...this.#messages, m];
    this.#emitMessages();
  }

  #emitMessages(): void {
    for (const fn of this.#messageSubs) fn(this.#messages);
  }
}

function supportsWebGpu(): boolean {
  try {
    return typeof navigator !== 'undefined' && 'gpu' in navigator && navigator.gpu !== null;
  } catch {
    return false;
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

const PERSONAL_HELPER_KEY = Symbol.for('alds.personalHelper');

/**
 * Per-AppContext singleton accessor. Mirrors the
 * getPresenceManager / getCallManager / getModerationManager pattern
 * established in v11.
 */
export function getPersonalHelper(ctx: AppContext): PersonalHelper {
  const bag = ctx as unknown as Record<symbol, PersonalHelper | undefined>;
  let helper = bag[PERSONAL_HELPER_KEY];
  if (helper === undefined) {
    helper = new PersonalHelper(ctx);
    bag[PERSONAL_HELPER_KEY] = helper;
  }
  return helper;
}
